// Thương hiệu NPP trên web (docs/KE_HOACH_THUONG_HIEU_NPP.md): ai thuộc NPP nào thấy màu + logo + tên NPP đó.
//   · Màu: chèn <style id="npp-brand"> với biến màu !important trên <html data-npp-brand> — thắng màu theo vai
//     trò (index.css), Glass (glass.css) và màu dịp (season.css); bảng màu dịp cũng tự tắt khi có data-npp-brand.
//     Màu trạng thái (--ok, --danger…) không đổi.
//   · Logo / tên: components/ui/logo.tsx đọc useBrand(). Tiêu đề tab + biểu tượng tab đổi theo NPP.
//   · Nhớ thương hiệu lần cuối trên máy (localStorage) cho trang Đăng nhập lần sau.
// NPP chưa đặt màu / logo → không đổi gì (giao diện ShopTrack như cũ).

import { useSyncExternalStore } from 'react'
import { brandCss, type BrandInfo } from '@shoptrack/common/brand'

export type { BrandInfo }

const STORAGE_KEY = 'st-npp-brand'
const STYLE_ID = 'npp-brand'
const DEFAULT_TITLE = 'ShopTrack'

/** NPP có đặt gì riêng chưa — chưa thì giữ nguyên ShopTrack (kể cả tên). */
export function hasBrandLook(b: BrandInfo | null | undefined): b is BrandInfo {
  return !!b && (!!b.colors || !!b.logoWide || !!b.logoSquare)
}

let current: BrandInfo | null = null
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((fn) => fn())

export function getBrand(): BrandInfo | null {
  return current
}

export function subscribeBrand(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Thương hiệu đang áp (null = ShopTrack mặc định). */
export function useBrand(): BrandInfo | null {
  return useSyncExternalStore(subscribeBrand, getBrand, getBrand)
}

const sameBrand = (a: BrandInfo | null, b: BrandInfo | null) => JSON.stringify(a) === JSON.stringify(b)

/** CSS biến màu của thương hiệu đang áp (packages/common/src/brand.ts). */
function cssOf(b: BrandInfo): string {
  return b.colors ? brandCss(b.colors) : ''
}

function setIcons(href: string | null) {
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="apple-touch-icon"]'))
  for (const l of links) {
    if (!l.dataset.stDefault) l.dataset.stDefault = l.getAttribute('href') ?? ''
    const next = href ?? l.dataset.stDefault
    if (l.getAttribute('href') !== next) l.setAttribute('href', next)
    // Ảnh logo NPP là PNG — bỏ khai SVG của biểu tượng gốc khi đổi
    if (href && l.type === 'image/svg+xml') l.type = 'image/png'
    if (!href && l.dataset.stDefault.endsWith('.svg')) l.type = 'image/svg+xml'
  }
}

function setTitle(name: string) {
  // Trang in phiếu tạm đổi document.title — chỉ đổi khi đang là tên mặc định / tên NPP cũ
  if (document.title === DEFAULT_TITLE || document.title === current?.name || document.title === '') document.title = name
  document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', name)
}

/** Áp thương hiệu (null = về ShopTrack). Gọi lặp lại với cùng giá trị không làm gì. */
export function applyBrand(next: BrandInfo | null, opts: { remember?: boolean } = {}): void {
  const b = hasBrandLook(next) ? next : null
  if (opts.remember !== false) remember(b)
  if (sameBrand(b, current)) return
  const root = document.documentElement
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null
  const css = b ? cssOf(b) : ''
  if (css) {
    if (!style) {
      style = document.createElement('style')
      style.id = STYLE_ID
      document.head.appendChild(style)
    }
    style.textContent = css
    root.setAttribute('data-npp-brand', b!.nppId)
    root.toggleAttribute('data-npp-menu', !!b!.colors?.menu)
  } else {
    style?.remove()
    root.removeAttribute('data-npp-brand')
    root.removeAttribute('data-npp-menu')
  }
  setTitle(b ? b.name : DEFAULT_TITLE)
  setIcons(b?.logoSquare ?? null)
  current = b
  emit()
}

function remember(b: BrandInfo | null) {
  try {
    if (b) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(b))
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // trình duyệt chặn lưu → lần sau trang Đăng nhập hiện ShopTrack chung
  }
}

/** Thương hiệu đã áp lần cuối trên máy này — trang Đăng nhập dùng khi chưa biết người dùng thuộc NPP nào. */
export function rememberedBrand(): BrandInfo | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const b = JSON.parse(raw) as BrandInfo
    return hasBrandLook(b) && typeof b.nppId === 'string' && typeof b.name === 'string' ? b : null
  } catch {
    return null
  }
}
